import axios from 'axios';
import { Config } from '../config';

const getAxiosConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'access_token': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
};

class VulnApiService {
  handleError(error) {
    const message =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      error.message;
    throw new Error(message);
  }

  // Attack surface management
  //
  // Under /vuln because ASM is exposure management — the module grew
  // continuous, it did not become a second product.

  async getAsmChanges(customerId, { sinceDays = 30, acknowledged } = {}) {
    const params = new URLSearchParams({ customer_id: customerId, since_days: sinceDays });
    if (acknowledged !== undefined) params.set('acknowledged', acknowledged);
    const { data } = await axios.get(
      `${Config.APIBaseURL}/vuln/asm/changes?${params}`, getAxiosConfig());
    return data;
  }

  async acknowledgeAsmChange(changeId) {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/vuln/asm/changes/${changeId}/acknowledge`, {}, getAxiosConfig());
    return data;
  }

  async getAsmNames(customerId) {
    const { data } = await axios.get(
      `${Config.APIBaseURL}/vuln/asm/names?customer_id=${customerId}`, getAxiosConfig());
    return data;
  }

  async getAsmClaims(customerId, { status, runId } = {}) {
    const params = new URLSearchParams({ customer_id: customerId });
    if (status) params.set('status', status);
    if (runId) params.set('run_id', runId);
    const { data } = await axios.get(
      `${Config.APIBaseURL}/vuln/asm/claims?${params}`, getAxiosConfig());
    return data;
  }

  async getAsmRuns(customerId, limit = 20) {
    const params = new URLSearchParams({ limit });
    if (customerId) params.set('customer_id', customerId);
    const { data } = await axios.get(
      `${Config.APIBaseURL}/vuln/asm/runs?${params}`, getAxiosConfig());
    return data;
  }

  async createAsmRun(customerId, scope) {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/vuln/asm/runs`,
      { customer_id: customerId, scope }, getAxiosConfig());
    return data;
  }

  async executeAsmRun(runId) {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/vuln/asm/runs/${runId}/execute`, {}, getAxiosConfig());
    return data;
  }

  async getAsmSchedules(customerId) {
    const params = new URLSearchParams();
    if (customerId) params.set('customer_id', customerId);
    const { data } = await axios.get(
      `${Config.APIBaseURL}/vuln/asm/schedules?${params}`, getAxiosConfig());
    return data;
  }

  async createAsmSchedule(payload) {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/vuln/asm/schedules`, payload, getAxiosConfig());
    return data;
  }

  async updateAsmSchedule(scheduleId, payload) {
    const { data } = await axios.patch(
      `${Config.APIBaseURL}/vuln/asm/schedules/${scheduleId}`, payload, getAxiosConfig());
    return data;
  }

  // Customers (shared endpoint)

  async getCustomers() {
    try {
      const response = await axios.get(
        `${Config.APIBaseURL}/customers/`,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  // Asset Groups

  async getAssetGroups(customerId) {
    try {
      const params = new URLSearchParams();
      if (customerId) params.append('customer_id', customerId);
      const response = await axios.get(
        `${Config.APIBaseURL}/vuln/asset-groups?${params.toString()}`,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async createAssetGroup(data) {
    try {
      const response = await axios.post(
        `${Config.APIBaseURL}/vuln/asset-groups`,
        data,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateAssetGroup(groupId, data) {
    try {
      const response = await axios.patch(
        `${Config.APIBaseURL}/vuln/asset-groups/${groupId}`,
        data,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async deleteAssetGroup(groupId) {
    try {
      const response = await axios.delete(
        `${Config.APIBaseURL}/vuln/asset-groups/${groupId}`,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  // Assets

  async getAssets(customerId, { assetGroupId, skip = 0, limit = 100 } = {}) {
    try {
      const params = new URLSearchParams({ skip, limit });
      if (customerId) params.append('customer_id', customerId);
      if (assetGroupId) params.append('asset_group_id', assetGroupId);
      const response = await axios.get(
        `${Config.APIBaseURL}/vuln/assets?${params.toString()}`,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async bulkAssignGroup(assetIds, assetGroupId) {
    try {
      const response = await axios.post(
        `${Config.APIBaseURL}/vuln/assets/bulk-assign-group`,
        { asset_ids: assetIds, asset_group_id: assetGroupId },
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getAsset(assetId) {
    try {
      const response = await axios.get(
        `${Config.APIBaseURL}/vuln/assets/${assetId}`,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async createAsset(data) {
    try {
      const response = await axios.post(
        `${Config.APIBaseURL}/vuln/assets`,
        data,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateAsset(assetId, data) {
    try {
      const response = await axios.patch(
        `${Config.APIBaseURL}/vuln/assets/${assetId}`,
        data,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async deleteAsset(assetId) {
    try {
      const response = await axios.delete(
        `${Config.APIBaseURL}/vuln/assets/${assetId}`,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  // Agents

  async getAgents() {
    try {
      const response = await axios.get(
        `${Config.APIBaseURL}/vuln/agents`,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  // Scans

  async getScans(customerId, { assetGroupId, skip = 0, limit = 100 } = {}) {
    try {
      const params = new URLSearchParams({ skip, limit });
      if (customerId) params.append('customer_id', customerId);
      if (assetGroupId) params.append('asset_group_id', assetGroupId);
      const response = await axios.get(
        `${Config.APIBaseURL}/vuln/scans?${params.toString()}`,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getScan(scanId) {
    try {
      const response = await axios.get(
        `${Config.APIBaseURL}/vuln/scans/${scanId}`,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async createScanFromAssets(data) {
    try {
      const response = await axios.post(
        `${Config.APIBaseURL}/vuln/scans/from-assets`,
        data,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async createScan(data) {
    try {
      const response = await axios.post(
        `${Config.APIBaseURL}/vuln/scans`,
        data,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async executeScan(scanId) {
    try {
      const response = await axios.post(
        `${Config.APIBaseURL}/vuln/scans/${scanId}/execute`,
        {},
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async pauseScan(scanId) {
    try {
      const response = await axios.post(
        `${Config.APIBaseURL}/vuln/scans/${scanId}/pause`,
        {},
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async resumeScan(scanId) {
    try {
      const response = await axios.post(
        `${Config.APIBaseURL}/vuln/scans/${scanId}/resume`,
        {},
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async stopScan(scanId) {
    try {
      const response = await axios.post(
        `${Config.APIBaseURL}/vuln/scans/${scanId}/stop`,
        {},
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async deleteScan(scanId) {
    try {
      const response = await axios.delete(
        `${Config.APIBaseURL}/vuln/scans/${scanId}`,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  // Findings

  async getFindings(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.customer_id) queryParams.append('customer_id', params.customer_id);
      if (params.scan_id) queryParams.append('scan_id', params.scan_id);
      if (params.asset_id) queryParams.append('asset_id', params.asset_id);
      if (params.asset_group_id) queryParams.append('asset_group_id', params.asset_group_id);
      if (params.severity) queryParams.append('severity', params.severity);
      if (params.status) queryParams.append('status', params.status);
      queryParams.append('skip', params.skip ?? 0);
      queryParams.append('limit', params.limit ?? 100);

      const response = await axios.get(
        `${Config.APIBaseURL}/vuln/findings?${queryParams.toString()}`,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getFinding(findingId) {
    try {
      const response = await axios.get(
        `${Config.APIBaseURL}/vuln/findings/${findingId}`,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateFinding(findingId, data) {
    try {
      const response = await axios.patch(
        `${Config.APIBaseURL}/vuln/findings/${findingId}`,
        data,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async bulkUpdateStatus(findingIds, status) {
    try {
      const response = await axios.post(
        `${Config.APIBaseURL}/vuln/findings/bulk-status`,
        { finding_ids: findingIds, status },
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  // Dashboard

  async getDashboard(customerId, assetGroupId) {
    try {
      const params = new URLSearchParams();
      if (customerId) params.append('customer_id', customerId);
      if (assetGroupId) params.append('asset_group_id', assetGroupId);
      const response = await axios.get(
        `${Config.APIBaseURL}/vuln/dashboard?${params.toString()}`,
        getAxiosConfig(),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }
}

export const vulnApi = new VulnApiService();
export default vulnApi;
